import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clients, getRandomClient, type Client } from '@/mocks/clients';

const countryFlags: Record<string, string> = {
  'Bulgaria': '🇧🇬',
  'Germany': '🇩🇪',
  'France': '🇫🇷',
  'Italy': '🇮🇹',
  'Spain': '🇪🇸',
  'UK': '🇬🇧',
  'Netherlands': '🇳🇱',
  'Poland': '🇵🇱',
  'Romania': '🇷🇴',
  'Greece': '🇬🇷',
  'Sweden': '🇸🇪',
  'China': '🇨🇳',
  'Japan': '🇯🇵',
  'India': '🇮🇳',
  'South Korea': '🇰🇷',
  'Singapore': '🇸🇬',
  'UAE': '🇦🇪',
  'Saudi Arabia': '🇸🇦',
  'Brazil': '🇧🇷',
  'Mexico': '🇲🇽',
  'Argentina': '🇦🇷',
  'USA': '🇺🇸',
  'Canada': '🇨🇦',
  'Nigeria': '🇳🇬',
  'South Africa': '🇿🇦',
  'Egypt': '🇪🇬',
  'Australia': '🇦🇺',
};

export default function FloatingLiveToast() {
  const { t } = useTranslation();
  const [currentClient, setCurrentClient] = useState<Client>(clients[0]);
  const [visible, setVisible] = useState(true);
  const [entering, setEntering] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out
      setEntering(false);
      setVisible(false);

      setTimeout(() => {
        setCurrentClient(getRandomClient());
        setVisible(true);
        setTimeout(() => setEntering(true), 50);
      }, 400);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const flag = countryFlags[currentClient.country] || '🌍';
  const roi = Math.round((currentClient.profit / currentClient.investment) * 100);

  return (
    <div
      className={`fixed bottom-6 left-4 z-50 max-w-[280px] transition-all duration-500 ease-out ${
        visible && entering
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-4'
      }`}
    >
      <div className="bg-[#0d1a0d] border border-green-500/40 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg shadow-green-900/30">
        {/* Live dot */}
        <div className="flex-shrink-0">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
        </div>

        {/* Avatar */}
        <img
          src={currentClient.avatar}
          alt={currentClient.name}
          className="w-9 h-9 rounded-full bg-white/5 flex-shrink-0"
        />

        {/* Info */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-white font-bold text-xs font-['Rajdhani'] truncate">
              {currentClient.name}
            </span>
            <span className="text-[11px]">{flag}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className="text-green-400 font-['Orbitron'] font-black text-sm">
              +{currentClient.profit.toLocaleString()} €
            </span>
            <span className="text-green-600 text-[10px] font-['Rajdhani']">
              +{roi}%
            </span>
          </div>
          <div className="text-green-700 text-[10px] font-['Rajdhani'] mt-0.5">
            {t('toast.in')} {t('toast.days')}
          </div>
        </div>
      </div>
    </div>
  );
}